/**
 * The app's places. Navigation is the product here: the order is the order of the work -
 * see what is open, capture receipts, reconcile the card, close the period, report, then the
 * assistant for questions on the way, then settings.
 */
import {
  BarChart3,
  Camera,
  Car,
  ClipboardCheck,
  CreditCard,
  FileText,
  Inbox,
  LayoutDashboard,
  MessageCircle,
  Package,
  Settings,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";
import { canWrite } from "@/lib/api/workspaces";
import { useWorkspace } from "@/lib/session/workspace-context";
import { cn } from "@/lib/utils/cn";

// `as const` keeps the keys literal, so t(`nav.${key}`) stays type-checked against en.json.
const ITEMS = [
  { to: "/dashboard", icon: LayoutDashboard, key: "dashboard" },
  { to: "/capture", icon: Camera, key: "capture", writers: true },
  { to: "/documents", icon: FileText, key: "documents" },
  { to: "/mailbox", icon: Inbox, key: "mailbox", writers: true },
  { to: "/banking", icon: CreditCard, key: "banking" },
  { to: "/close", icon: ClipboardCheck, key: "close" },
  { to: "/reports", icon: BarChart3, key: "reports" },
  { to: "/assets", icon: Package, key: "assets" },
  { to: "/trips", icon: Car, key: "trips" },
  { to: "/assistant", icon: MessageCircle, key: "assistant" },
  { to: "/settings", icon: Settings, key: "settings" },
] as const satisfies ReadonlyArray<{
  to: string;
  icon: LucideIcon;
  key: string;
  /** Only for roles that capture receipts: an accountant or viewer has nothing to do there. */
  writers?: boolean;
}>;

export function AppNav({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  const { role } = useWorkspace();
  // By role, not plan: an owner whose trial ended still finds the page that says why (QA-0043).
  const items = ITEMS.filter((item) => !("writers" in item) || canWrite(role));
  return (
    <nav className="flex flex-col gap-0.5" aria-label={t("nav.main")}>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          data-testid={`nav-${item.key}`}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors",
              isActive
                ? "bg-accent font-medium text-accent-foreground"
                : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
            )
          }
        >
          <item.icon className="size-4 shrink-0" />
          {t(`nav.${item.key}`)}
        </NavLink>
      ))}
    </nav>
  );
}
